import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, User, Package, CalendarDays, FileText, Wallet, BookOpen, Megaphone, UserCog, LifeBuoy, Menu, X, LogOut } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import NotificationBell from "@/components/shared/NotificationBell";
import WhatsAppButton from "@/components/layout/WhatsAppButton";

const MENU = [
  { to: "/jamaah", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { to: "/jamaah/profil", label: "Profil Saya", icon: User },
  { to: "/jamaah/program", label: "Program Saya", icon: Package },
  { to: "/jamaah/manasik", label: "Manasik", icon: CalendarDays },
  { to: "/jamaah/dokumen", label: "Dokumen", icon: FileText },
  { to: "/jamaah/pembayaran", label: "Pembayaran", icon: Wallet },
  { to: "/jamaah/materi", label: "Materi", icon: BookOpen },
  { to: "/jamaah/pengumuman", label: "Pengumuman", icon: Megaphone },
  { to: "/jamaah/pembimbing", label: "Pembimbing", icon: UserCog },
];

function SidebarContent({ onNavigate }) {
  return (
    <nav className="flex flex-col gap-1 p-4" data-testid="jamaah-sidebar-nav">
      {MENU.map((m) => (
        <NavLink key={m.to} to={m.to} end={m.exact} onClick={onNavigate} data-testid={`jamaah-sidebar-${m.label.toLowerCase().replace(/\s+/g, "-")}`}
          className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-[#0F6B4F] text-white" : "text-[#1F2937] hover:bg-[#0F6B4F]/8"}`}>
          <m.icon className="h-4 w-4 shrink-0" /> {m.label}
        </NavLink>
      ))}
      <a href={`https://wa.me/${useSite().settings?.whatsapp || "6281234567890"}`} target="_blank" rel="noreferrer" data-testid="jamaah-sidebar-bantuan" className="flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-medium text-[#1F2937] hover:bg-[#0F6B4F]/8">
        <LifeBuoy className="h-4 w-4 shrink-0" /> Bantuan
      </a>
    </nav>
  );
}

export default function JamaahLayout() {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <div className="flex min-h-screen bg-[#F5F9F7]" data-testid="jamaah-layout">
      <aside className="hidden w-64 shrink-0 border-r border-slate-100 bg-white lg:block">
        <div className="flex h-16 items-center gap-2 border-b border-slate-100 px-5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#0F6B4F] to-[#1677B8] text-white font-heading font-bold">K</div>
          <span className="font-heading text-sm font-bold leading-tight text-[#1F2937]">KBIHU Muhammadiyah Surabaya</span>
        </div>
        <SidebarContent />
      </aside>

      <div className="flex flex-1 flex-col">
        <header className="flex h-16 items-center justify-between gap-4 border-b border-slate-100 bg-white px-4 sm:px-6">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button className="lg:hidden" data-testid="jamaah-mobile-menu-toggle" aria-label="Menu"><Menu className="h-5 w-5 text-[#1F2937]" /></button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 bg-white p-0">
              <div className="flex h-16 items-center justify-between border-b border-slate-100 px-5">
                <span className="font-heading text-base font-bold text-[#1F2937]">KBIHU Muhammadiyah Surabaya</span>
                <button onClick={() => setMobileOpen(false)} data-testid="jamaah-mobile-menu-close"><X className="h-5 w-5" /></button>
              </div>
              <SidebarContent onNavigate={() => setMobileOpen(false)} />
            </SheetContent>
          </Sheet>
          <span className="font-heading text-sm font-semibold text-[#1F2937] lg:hidden">Dashboard Jamaah</span>
          <div className="flex items-center gap-3">
            <NotificationBell />
            <div className="flex items-center gap-2 border-l border-slate-100 pl-3">
              <p className="hidden text-sm font-medium text-[#1F2937] sm:block" data-testid="jamaah-user-name">{user?.name}</p>
              <Button variant="ghost" size="icon" data-testid="jamaah-logout-button" onClick={() => logout().then(() => navigate("/"))} className="text-[#64748B] hover:text-red-600">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-x-hidden p-4 sm:p-6"><Outlet /></main>
      </div>
      <WhatsAppButton label="Hubungi Admin" message="Assalamu'alaikum, saya ingin bertanya seputar pendaftaran saya." />
    </div>
  );
}
